import { useState, useCallback } from 'react';
import { uploadVideo } from '@/api/videoApi';

interface UseVideoUploadReturn {
  /** Avvia l'upload del file */
  upload: (file: File) => Promise<string | null>;
  /** Progresso percentuale (0-100) */
  progress: number;
  /** True se l'upload è in corso */
  uploading: boolean;
  /** Messaggio di errore, o null */
  error: string | null;
  /** ID del video dopo il caricamento riuscito, null altrimenti */
  videoId: string | null;
  /** Riporta lo stato allo stato iniziale */
  reset: () => void;
}

/**
 * Hook personalizzato per gestire l'upload di un video.
 * Fornisce progresso, errori e l'ID del video creato.
 */
export function useVideoUpload(): UseVideoUploadReturn {
  const [progress, setProgress] = useState<number>(0);
  const [uploading, setUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [videoId, setVideoId] = useState<string | null>(null);

  const upload = useCallback(async (file: File): Promise<string | null> => {
    setUploading(true);
    setProgress(0);
    setError(null);
    setVideoId(null);

    try {
      const result = await uploadVideo(file, (pct) => {
        setProgress(pct);
      });
      setVideoId(result.id);
      setProgress(100);
      return result.id;
    } catch (err: any) {
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        'Errore durante il caricamento del video.';
      setError(message);
      return null;
    } finally {
      setUploading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setProgress(0);
    setUploading(false);
    setError(null);
    setVideoId(null);
  }, []);

  return { upload, progress, uploading, error, videoId, reset };
}